import { DASH } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';

export interface IImpressionsByUnitDimension {
  demographic_impressions: number;
  impressions: number;
  unit_count: number;
  unit_height: number;
  unit_language: string[];
  unit_width: number;
}
export interface IImpressionsByNetworkID {
  demographic_impressions: number;
  impressions: number;
  network_id: number;
}

export interface IImpressionsByUnitID {
  demographic_impressions: number;
  impressions: number;
  unit_id: number;
}

export interface IUnitCount {
  [key: number]: number;
}

export interface IDeliveryStatisticsAPI {
  unit_count: number;
  demographic_unit_count: number;
  site_count: number;
  demographic_site_count: number;
  impressions: number;
  demographic_impressions: number;
  unit_physical_count: number;
  impressions_by_unit_dimensions: IImpressionsByUnitDimension[];
  impressions_by_unit_id: IImpressionsByUnitID[];
  impressions_by_network_id: IImpressionsByNetworkID[];
  unit_count_by_owner_id: IUnitCount; // owner_id: unit_count
  unit_physical_count_by_owner_id: IUnitCount; // owner_id: unit_physical_count
}

export default class DeliveryStatistics {
  unitCount?: number;
  demographicUnitCount?: number;
  siteCount?: number;
  demographicsSiteCount?: number;
  impressions?: number;
  demographicImpressions?: number;
  unitPhysicalCount?: number;
  impressionsByUnitDimensions?: IImpressionsByUnitDimension[];
  impressionsByUnitID?: IImpressionsByUnitID[];
  impressionsByNetworkID?: IImpressionsByNetworkID[];
  unitCountByOwnerID?: IUnitCount;
  unitPhysicalCountByOwnerID?: IUnitCount;
  onePlayPerHourDemographicImpressions?: number;
  onePlayPerHourImpressions?: number;

  constructor(
    unitCount?: number,
    demographicUnitCount?: number,
    siteCount?: number,
    demographicsSiteCount?: number,
    impressions?: number,
    demographicImpressions?: number,
    unitPhysicalCount?: number,
    impressionsByUnitDimensions?: IImpressionsByUnitDimension[],
    impressionsByUnitID?: IImpressionsByUnitID[],
    impressionsByNetworkID?: IImpressionsByNetworkID[],
    unitCountByOwnerID?: IUnitCount,
    unitPhysicalCountByOwnerID?: IUnitCount,
  ) {
    this.unitCount = unitCount;
    this.demographicUnitCount = demographicUnitCount;
    this.siteCount = siteCount;
    this.demographicsSiteCount = demographicsSiteCount;
    this.impressions = impressions;
    this.demographicImpressions = demographicImpressions;
    this.unitPhysicalCount = unitPhysicalCount;
    this.impressionsByUnitDimensions = impressionsByUnitDimensions || [];
    this.impressionsByUnitID = impressionsByUnitID || [];
    this.impressionsByNetworkID = impressionsByNetworkID || [];
    this.unitCountByOwnerID = unitCountByOwnerID || {};
    this.unitPhysicalCountByOwnerID = unitPhysicalCountByOwnerID || {};
  }

  get demographicImpressionsFormatted(): string {
    if (formatNumber(this.demographicImpressions, '0a').length === 2) {
      return formatNumber(this.demographicImpressions, '0.0a');
    }
    return formatNumber(this.demographicImpressions, '0a');
  }

  get siteCountFormatted(): string {
    return this.siteCount != null ? formatNumber(this.siteCount, '0,0') : DASH;
  }

  get unitCountFormatted(): string {
    return this.unitCount != null ? formatNumber(this.unitCount, '0,0') : DASH;
  }

  get unitPhysicalCountFormatted(): string {
    return this.unitPhysicalCount != null ? formatNumber(this.unitPhysicalCount, '0,0') : DASH;
  }

  toApi(): IDeliveryStatisticsAPI {
    return {
      unit_count: this.unitCount,
      demographic_unit_count: this.demographicUnitCount,
      site_count: this.siteCount,
      demographic_site_count: this.demographicsSiteCount,
      impressions: this.impressions,
      demographic_impressions: this.demographicImpressions,
      unit_physical_count: this.unitPhysicalCount,
      impressions_by_unit_dimensions: this.impressionsByUnitDimensions,
      impressions_by_unit_id: this.impressionsByUnitID,
      impressions_by_network_id: this.impressionsByNetworkID,
      unit_count_by_owner_id: this.unitCountByOwnerID,
      unit_physical_count_by_owner_id: this.unitPhysicalCountByOwnerID,
    };
  }

  static fromApi(apiObject: IDeliveryStatisticsAPI): DeliveryStatistics {
    return new DeliveryStatistics(
      apiObject.unit_count,
      apiObject.demographic_unit_count,
      apiObject.site_count,
      apiObject.demographic_site_count,
      apiObject.impressions,
      apiObject.demographic_impressions,
      apiObject.unit_physical_count,
      apiObject.impressions_by_unit_dimensions,
      apiObject.impressions_by_unit_id,
      apiObject.impressions_by_network_id,
      apiObject.unit_count_by_owner_id,
      apiObject.unit_physical_count_by_owner_id,
    );
  }
}
